import { logger } from '../Logger';
import { createError } from 'util/FormatError';
import { RealmOrigin } from 'types/RealmOrigin';

declare const self: Window &
  typeof globalThis & {
    _env_: {
      [id: string]: string;
    };
  };

/**
 * Tries to gather the NODE_ENV variable. If not found the fallback is
 * 'production'.
 */
const nodeEnv = (): string => {
  const varName = 'NODE_ENV';
  if (process.env && process.env[varName]) {
    return process.env[varName];
  }

  // Self is pointing to the window object and is also working
  // in both a serviceWorker or a webWorker
  // @see https://developer.mozilla.org/en-US/docs/Web/API/Window/self
  if (self._env_ && self._env_[varName]) {
    return self._env_[varName];
  }
  return 'production';
};

/**
 * In production we want to log non existing variables. However, they
 * should not contain the term 'DEVELOPMENT'.
 */
const logNonExisting = (varName: string, useLoggerObject = true): void => {
  if (nodeEnv() !== 'development' && varName.includes('DEVELOPMENT')) {
    return;
  }
  if (nodeEnv() === 'test') {
    return;
  }
  const logMessage = `Try to load a non-existing environment variable "${varName}"`;
  useLoggerObject ? logger.warn(logMessage) : console.warn(logMessage);
};

/**
 * Because we cannot load the process.env var in production we added a file with window._env_ object that holds all env vars
 *
 * For production, we should load the data from there and for development from process.env
 *
 * @param varName
 * @param useLoggerObject Log via our logger object or via the default console object
 */
export const envVar = (varName: string, useLoggerObject = true): string | undefined => {
  if (process.env && process.env[varName]) {
    return process.env[varName];
  }

  // Self is pointing to the window object and is also working
  // in both a serviceWorker or a webWorker
  // @see https://developer.mozilla.org/en-US/docs/Web/API/Window/self
  if (self._env_ && self._env_[varName]) {
    return self._env_[varName];
  }

  logNonExisting(varName, useLoggerObject);
  return undefined;
};

/**
 * Check whether an env var is true
 *
 * @param varName
 */
export const envVarIsTrue = (varName: string): boolean => {
  const value = envVar(varName);

  if (value === undefined) {
    return false;
  }

  return value === 'true';
};

/**
 * Get the env Urls, constructed with a protocol
 */
export const getEnvUrls = (url?: string): { apiUrl: string; wsUrl: string; url: string } => {
  const apiUrl = url ?? envVar('STORRO_API_URL');
  const isDevelopment = process.env && process.env.NODE_ENV === 'development';
  const developmentProxySSL = envVarIsTrue('DEVELOPMENT_PROXY_SSL');
  const isSSL = envVarIsTrue('SSL');
  const httpProtocol = isSSL ? 'https://' : 'http://';
  const websocketProtocol = (!isDevelopment && isSSL) || (isDevelopment && developmentProxySSL) ? 'wss://' : 'ws://';
  const apiUrlWithProtocol = isDevelopment && !url ? `${httpProtocol}${location.host}/api` : `${httpProtocol}${apiUrl}`;
  const wsUrlWithProtocol = `${websocketProtocol}${apiUrl}`;

  return {
    apiUrl: apiUrlWithProtocol,
    wsUrl: wsUrlWithProtocol,
    url: `${httpProtocol}${location.host}`,
  };
};

/**
 * Check if all env vars are exists and loaded
 */
export const validEnvVars = (): boolean => {
  const baseUrl = envVar('STORRO_API_URL');
  const isSSL = envVar('SSL');

  if (baseUrl === undefined || baseUrl === '') {
    logger.error(createError('ENV Variable "STORRO_API_URL" should be set'));
    return false;
  }

  if (isSSL === undefined || isSSL === '') {
    logger.error(createError('ENV Variable "SSL" should be set'));
    return false;
  }

  try {
    checkUriVariables();
  } catch (error) {
    logger.warn('URI variables failed: %s', JSON.stringify(error));
    return false;
  }

  return true;
};

/**
 * Go over the RealmOrigin values and check if the 'APP' and 'BINARIES'
 * environment variables are defined. If something is missing or set to '' an
 * Error is thrown.
 */
export function checkUriVariables(): void {
  const URITYPES = ['BINARIES'];
  for (const key of Object.values(RealmOrigin)) {
    const origin = key.toUpperCase();
    for (const type of URITYPES) {
      const variableName = `URI_${origin}_${type}`;
      const value = envVar(variableName, false);
      if (value === undefined || value === '') {
        throw Error(`${variableName} should be set.`);
      }
    }
  }
}
